// Guide & Tips — static reference cards. Ported verbatim from the legacy
// views.guide; the only change is the `(app)` mount-element signature.
export function guideView(app) {
  app.innerHTML = `
    <section class="space-y-6">
      <div>
        <h2 class="text-2xl font-bold text-sage-800 mb-1">Guide & Tips</h2>
        <p class="text-sage-600 text-sm">Memory tricks and patterns to help you internalize Italian verbs faster.</p>
      </div>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">📐 Regular verbs — the foundation</h3>
        <p class="text-sm text-sage-700 mb-3">Italian has three regular conjugation patterns based on the infinitive ending:</p>
        <div class="grid sm:grid-cols-3 gap-3 text-sm">
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-ARE verbs</p>
            <p class="text-xs text-sage-500 mb-2">parlare (to speak)</p>
            <p class="font-mono text-xs leading-relaxed">o, i, a,<br>iamo, ate, ano</p>
          </div>
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-ERE verbs</p>
            <p class="text-xs text-sage-500 mb-2">credere (to believe)</p>
            <p class="font-mono text-xs leading-relaxed">o, i, e,<br>iamo, ete, ono</p>
          </div>
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-IRE verbs</p>
            <p class="text-xs text-sage-500 mb-2">dormire (to sleep)</p>
            <p class="font-mono text-xs leading-relaxed">o, i, e,<br>iamo, ite, ono</p>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3">💡 Tip: -ERE and -IRE verbs share their endings except in the voi form (-ete vs -ite).</p>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🔄 -isc- verbs — the boot</h3>
        <p class="text-sm text-sage-700 mb-3">Many -ire verbs slot an <b>-isc-</b> into the stem in every present form EXCEPT noi and voi (the heel of the boot). If you draw a line around the four -isc- forms, it looks like a boot.</p>
        <div class="bg-sage-50 rounded-lg p-3 text-sm font-mono mb-3 max-w-xs">
          <div class="grid grid-cols-2 gap-1">
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">capisco</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">capisci</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">capisce</div>
            <div class="px-2 py-1">capiamo</div>
            <div class="px-2 py-1">capite</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">capiscono</div>
          </div>
        </div>
        <ul class="text-sm text-sage-700 space-y-1 list-disc pl-5">
          <li><b>Takes -isc-</b>: capire, finire, preferire, pulire, costruire</li>
          <li><b>Stays plain</b>: dormire, partire, sentire, aprire, offrire</li>
          <li>The infix lands in <b>io, tu, lui/lei</b> and <b>loro</b> — never in noi / voi</li>
          <li>Only -ire verbs do this; -are and -ere never take -isc-</li>
        </ul>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">👤 The "-go" verbs — irregular io & loro</h3>
        <p class="text-sm text-sage-700 mb-3">A group of verbs takes a <b>-go</b> in the io form (and <b>-gono</b> in loro), but is otherwise regular:</p>
        <div class="bg-sage-50 rounded-lg p-3 text-sm font-mono mb-3">
          venire → ven<span class="text-red-600 font-bold">go</span> · tenere → ten<span class="text-red-600 font-bold">go</span> · salire → sal<span class="text-red-600 font-bold">go</span> · rimanere → riman<span class="text-red-600 font-bold">go</span> · porre → pon<span class="text-red-600 font-bold">go</span> · valere → val<span class="text-red-600 font-bold">go</span>
        </div>
        <p class="text-xs text-sage-500">💡 Memorize the list as a chant: "vengo, tengo, salgo, rimango, pongo, valgo."</p>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">⚡ Fully irregular — the big four</h3>
        <p class="text-sm text-sage-700 mb-3">Four verbs are so common and so irregular they deserve dedicated memorization: <b>essere</b>, <b>avere</b>, <b>fare</b>, and <b>stare</b>. Strange fact: the past participle <i>stato</i> belongs to both <i>essere</i> and <i>stare</i> (sono stato) — context tells them apart.</p>
        <ul class="text-sm text-sage-700 space-y-1 list-disc pl-5">
          <li><b>essere vs stare</b>: essere covers identity and most descriptions (sono alto, sono stanco); stare covers wellbeing, location and the progressive (sto bene, sto a casa, sto leggendo).</li>
          <li><b>sapere vs conoscere</b>: sapere for facts and skills (so nuotare), conoscere for people and places (conosco Maria).</li>
          <li><b>stare per + infinitive</b> = "about to" do something. Sto per mangiare = I'm about to eat.</li>
        </ul>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">📅 The three core tenses</h3>
        <div class="space-y-3 text-sm text-sage-700">
          <div>
            <p class="font-bold text-sage-800">Presente — happening now or habitually</p>
            <p>"Io parlo italiano" = I speak Italian / I am speaking Italian.</p>
          </div>
          <div>
            <p class="font-bold text-sage-800">Passato Prossimo — completed actions in the past</p>
            <p>"Ho parlato con Maria ieri" = I spoke with Maria yesterday. Built with <i>avere</i> or <i>essere</i> + the past participle.</p>
          </div>
          <div>
            <p class="font-bold text-sage-800">Futuro Semplice — what will happen</p>
            <p>"Parlerò con Maria domani" = I will speak with Maria tomorrow. <b>Quick win:</b> take the infinitive, drop the final -e, and add ò, ai, à, emo, ete, anno — for -are verbs the a shifts to e (parlare → parlerò).</p>
          </div>
        </div>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🪞 Reflexive verbs</h3>
        <p class="text-sm text-sage-700 mb-2">Verbs ending in <b>-si</b> are reflexive — the action is done to oneself. Conjugate normally, then put the matching pronoun (mi, ti, si, ci, vi, si) before the verb.</p>
        <div class="bg-sage-50 rounded-lg p-3 font-mono text-sm">
          alzarsi → mi alzo, ti alzi, si alza…
        </div>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🎯 Study strategy</h3>
        <ol class="text-sm text-sage-700 space-y-2 list-decimal pl-5">
          <li><b>Start with the top 50.</b> They cover ~70% of everyday speech.</li>
          <li><b>Learn one tense at a time.</b> Master Presente before moving on.</li>
          <li><b>Drill the "-go" verbs as a set.</b> They share a single shortcut.</li>
          <li><b>Use the trainer daily.</b> Even 5 minutes builds strong recall.</li>
          <li><b>Track mistakes.</b> Verbs you miss often deserve flashcard time.</li>
        </ol>
      </article>
    </section>
  `;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
